import { Injectable } from '@angular/core';
import { DataRepository, WorkspaceDatabaseKeys } from '@database/workspace';
import {
	CardContentModel,
	ColumnModel,
	DataModel,
	DateTimeService,
	fileBooleanMappings,
	FormActionTypeEnum,
	GroupedColumnsModel,
	incrementSampleNumber,
	listToDictionaryObject,
	PrimaryKeyValueModel,
	TableTypeEnum
} from '@shared';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ColumnInformationService } from '../../../domain/dynamic-form/validations/services/column-information.service';
import { QueryParamsProvider } from '../../routing/services/query-params.provider';
import { DataByTableTypeProvider } from './data-by-table-type.provider';
import { TableTypeProvider } from './table-type.provider';

@Injectable()
export class SamplesService {
	// todo: [technical] add an orchestrator for injecting data repository based on table type
	public constructor(
		private dataRepository: DataRepository,
		private tableTypeProvider: TableTypeProvider,
		private queryParamsProvider: QueryParamsProvider,
		private dataByTableTypeProvider: DataByTableTypeProvider,
		private dateTimeService: DateTimeService,
		private columnInformationService: ColumnInformationService
	) {}

	public getNewSampleNumber$(projectNumber: string, primaryKeyValue: PrimaryKeyValueModel[]): Observable<string> {
		return this.dataByTableTypeProvider
			.getNewSampleNumber$(this.queryParamsProvider.parentTableName, projectNumber, primaryKeyValue)
			.pipe(
				map(([lastSample, lastStandard]) => {
					if (!lastSample) {
						lastSample = '1';
					} else {
						lastSample = incrementSampleNumber(lastSample.toString());
					}

					if (!lastStandard) {
						lastStandard = '1';
					} else {
						lastStandard = incrementSampleNumber(lastStandard.toString());
					}

					return parseInt(lastSample.match(/\d+$/)[0], 10) > parseInt(lastStandard.match(/\d+$/)[0], 10)
						? lastSample
						: lastStandard;
				})
			);
	}

	public getSampleCardItems(
		groupedColumns: GroupedColumnsModel,
		td: DataModel,
		excludedProperties: string[],
		isStandard: boolean
	): CardContentModel[] {
		const dataVisibleColumnsSamples = groupedColumns.sampleColumns.filter(
			(dataColumn: ColumnModel) => fileBooleanMappings[dataColumn.display_column]
		);
		const dataVisibleColumnsStandards = groupedColumns.standardColumns.filter(
			(dataColumn: ColumnModel) => fileBooleanMappings[dataColumn.display_column]
		);

		const dataColumnsDictionarySamples = listToDictionaryObject(
			WorkspaceDatabaseKeys.columns.columnName,
			dataVisibleColumnsSamples
		);
		const dataColumnsDictionaryStandards = listToDictionaryObject(
			WorkspaceDatabaseKeys.columns.columnName,
			dataVisibleColumnsStandards
		);

		return (isStandard ? dataVisibleColumnsStandards : dataVisibleColumnsSamples)
			.filter((dataColumn: ColumnModel) => !excludedProperties.includes(dataColumn.column_name))
			.filter((dataColumn: ColumnModel) =>
				isStandard
					? !dataColumnsDictionaryStandards[dataColumn.column_label]
					: !dataColumnsDictionarySamples[dataColumn.column_label]
			)
			.map((dataColumn: ColumnModel) => ({
				key: dataColumn.column_name,
				value: this.columnInformationService.isColumnTypeTime(dataColumn)
					? this.dateTimeService.fromFusionTimeFieldsFormat(td[dataColumn.column_name])
					: td[dataColumn.column_name],
				label: dataColumn.column_label,
				order: dataColumn.display_order ? dataColumn.display_order : 0,
				valueType: this.dateTimeService.getColumnDataType(dataColumn.edit_style, dataColumn.data_type)
			}))
			.sort((a, b) => a.order - b.order);
	}

	public isQcSampleCodeDuplicate$(sampleType: string, depthFrom: number, depthTo: number): Observable<boolean> {
		return this.tableTypeProvider
			.getTableType$(this.queryParamsProvider.activeTableName, this.queryParamsProvider.workfileId)
			.pipe(
				switchMap((tableTypes: string[]) => {
					if (
						!(
							tableTypes.includes(TableTypeEnum.flatSampleTable) ||
							tableTypes.includes(TableTypeEnum.sampleTable)
						) ||
						this.queryParamsProvider.formActionType() !== FormActionTypeEnum.duplicate
					) {
						return of(false);
					}

					return this.isQcDup$(
						this.queryParamsProvider.childTableName,
						this.queryParamsProvider.parentTableName,
						this.queryParamsProvider.entryId,
						this.queryParamsProvider.childEntryId,
						this.queryParamsProvider.workfileId,
						this.queryParamsProvider.projectNumber,
						sampleType,
						depthFrom,
						depthTo
					);
				})
			);
	}

	public isQcDup$(
		childTableName: string,
		parentableName: string,
		parentEntryId: string,
		childEntryId: string,
		workfileId: string,
		projectNumber: string,
		sampleType: string,
		depthFrom: number,
		depthTo: number
	): Observable<boolean> {
		return combineLatest([
			this.dataByTableTypeProvider.getOutsideIntervalData$(
				childTableName,
				workfileId,
				projectNumber,
				parentableName,
				parentEntryId,
				childEntryId,
				depthFrom,
				depthTo
			),
			this.dataRepository
				.getTableEntry$(
					this.queryParamsProvider.activeTableName,
					projectNumber,
					Number(this.queryParamsProvider.activeTableId)
				)
				.pipe(
					switchMap((currentOriginalSample: DataModel) =>
						this.dataByTableTypeProvider.getParentSampleNumberLinkedData$(
							childTableName,
							workfileId,
							projectNumber,
							parentableName,
							parentEntryId,
							childEntryId,
							currentOriginalSample[WorkspaceDatabaseKeys.columns.parentSampleNumber]
						)
					)
				)
		]).pipe(
			map(([outsideIntervalData, sampleLinkedData]) => {
				const intervalQcDups = outsideIntervalData?.some(
					(intervalEntry: DataModel) => intervalEntry.assay_sample_type_code === sampleType
				);
				const parenSampleNumberQcDups = sampleLinkedData?.some(
					(intervalEntry: DataModel) => intervalEntry.assay_sample_type_code === sampleType
				);

				return intervalQcDups || parenSampleNumberQcDups;
			})
		);
	}
}
